/**
 * CallingDisplayRegistry — lightweight shared display registry and name resolver
 * for Helix 2000 original callings.
 *
 * Architecture Invariants:
 * 1. Decouples player-facing display names ("Steadfast", "Astromancer", etc.)
 *    from runtime/save/wire IDs ('warrior', 'mage', etc.).
 * 2. Save/wire IDs, legacy job IDs, network state, and aiming contracts remain
 *    100% stable and untouched.
 * 3. Provides canonical resolver `formatCallingName(classId)` for all player-facing UI.
 */

import { CharacterClass } from '../CharacterTypes';

export interface CallingDisplayMeta {
  readonly id: CharacterClass;
  readonly name: string;
  readonly epithet: string;
  readonly icon: string;
  readonly color: string;
  readonly role: string;
  readonly description: string;
}

export const CALLING_DISPLAY_REGISTRY: Readonly<Record<CharacterClass, CallingDisplayMeta>> = {
  [CharacterClass.WARRIOR]: {
    id: CharacterClass.WARRIOR,
    name: 'Steadfast',
    epithet: 'The Steadfast',
    icon: '⚔️',
    color: '#c0392b',
    role: 'Melee Tank / Brawler',
    description: 'When the road narrows, you are the one who does not move. You meet the unknown face-first, turning courage into shelter for everyone who follows.',
  },
  [CharacterClass.MAGE]: {
    id: CharacterClass.MAGE,
    name: 'Astromancer',
    epithet: 'The Star-Touched',
    icon: '🔮',
    color: '#3498db',
    role: 'Ranged DPS / Buffer',
    description: 'Every strange light is an invitation. You follow wonder past the edge of certainty, listening for patterns hidden in impossible things.',
  },
  [CharacterClass.HUNTER]: {
    id: CharacterClass.HUNTER,
    name: 'Wayfinder',
    epithet: 'The Wayfinder',
    icon: '🏹',
    color: '#27ae60',
    role: 'Ranged DPS / Scout',
    description: 'No trail is truly empty to patient eyes. You belong to the long road, reading wind, tracks, and silence where others see only wilderness.',
  },
  [CharacterClass.PALADIN]: {
    id: CharacterClass.PALADIN,
    name: 'Dawnward',
    epithet: 'The Dawnward',
    icon: '🛡️',
    color: '#f1c40f',
    role: 'Tank / Healer',
    description: 'A small light is still a promise. You carry hope into places that have forgotten its shape and stand where the dark presses closest.',
  },
  [CharacterClass.ROGUE]: {
    id: CharacterClass.ROGUE,
    name: 'Unseen',
    epithet: 'The Unseen',
    icon: '🗡️',
    color: '#8e44ad',
    role: 'Melee DPS / Infiltrator',
    description: 'Some doors prefer a quieter kind of courage. You notice the loose stone, the guarded glance, and the second path nobody thought to watch.',
  },
  [CharacterClass.SHAMAN]: {
    id: CharacterClass.SHAMAN,
    name: 'Stormspeaker',
    epithet: 'The Stormspeaker',
    icon: '🌩️',
    color: '#00B3A4',
    role: 'Elemental Hybrid / Totemist',
    description: 'The sky has a voice. So does the stone. You listen where others command, walking beside the old tempers of rain, ember, and earth.',
  },
  [CharacterClass.PRIEST]: {
    id: CharacterClass.PRIEST,
    name: 'Lightbearer',
    epithet: 'The Lightbearer',
    icon: '✨',
    color: '#bdc3c7',
    role: 'Healer / Support',
    description: 'Mercy is brightest where it is hardest to offer. You tend the fragile flame in others, even when your own path passes through shadow.',
  },
  [CharacterClass.WARLOCK]: {
    id: CharacterClass.WARLOCK,
    name: 'Pactbound',
    epithet: 'The Pactbound',
    icon: '😈',
    color: '#7D3C98',
    role: 'Affliction Caster / Summoner',
    description: 'When the lantern gutters, something answers. You listen at doors others leave unopened, drawn by the knowledge and danger waiting beyond the firelight.',
  },
  [CharacterClass.DRUID]: {
    id: CharacterClass.DRUID,
    name: 'Wildhearted',
    epithet: 'The Wildhearted',
    icon: '🌿',
    color: '#6B8E23',
    role: 'Nature Caster / Restorer',
    description: 'Nothing living keeps only one shape. You move with root, rain, and restless green things, guarding the quiet agreements that hold the wild together.',
  },
  [CharacterClass.DEV]: {
    id: CharacterClass.DEV,
    name: 'Dev',
    epithet: 'The Architect',
    icon: '🧪',
    color: '#e67e22',
    role: 'Debug / Testing',
    description: 'Developer sandbox class with unrestricted test capabilities.',
  },
};

/** Historical and display aliases mapped to canonical CharacterClass. */
const CALLING_ALIASES: Readonly<Record<string, CharacterClass>> = {
  // Historical legacy class names
  ranger: CharacterClass.HUNTER,
  assassin: CharacterClass.ROGUE,
  // Calling display names as aliases (allows URL param or search by display name)
  steadfast: CharacterClass.WARRIOR,
  astromancer: CharacterClass.MAGE,
  wayfinder: CharacterClass.HUNTER,
  dawnward: CharacterClass.PALADIN,
  unseen: CharacterClass.ROGUE,
  stormspeaker: CharacterClass.SHAMAN,
  lightbearer: CharacterClass.PRIEST,
  pactbound: CharacterClass.WARLOCK,
  wildhearted: CharacterClass.DRUID,
};

/**
 * Resolves any class ID, alias, or display name to canonical CharacterClass.
 * Returns null if not recognized.
 */
export function resolveToCharacterClass(raw: string | CharacterClass | null | undefined): CharacterClass | null {
  if (!raw) return null;
  const normalized = String(raw).trim().toLowerCase();

  // Direct check against CharacterClass values
  for (const c of Object.values(CharacterClass)) {
    if (c === normalized) return c;
  }

  // Alias lookup
  if (normalized in CALLING_ALIASES) {
    return CALLING_ALIASES[normalized];
  }

  return null;
}

/**
 * Primary user-facing name resolver.
 * Converts a runtime class ID or enum (e.g. 'warrior', 'mage', 'ranger') into its
 * approved original Helix calling name (e.g. 'Steadfast', 'Astromancer', 'Wayfinder').
 *
 * Gracefully falls back to capitalizing unknown strings, or returns 'Unknown' if empty.
 */
export function formatCallingName(classId: string | CharacterClass | null | undefined): string {
  if (!classId) return 'Unknown';
  const resolved = resolveToCharacterClass(classId);
  if (resolved && CALLING_DISPLAY_REGISTRY[resolved]) {
    return CALLING_DISPLAY_REGISTRY[resolved].name;
  }
  const str = String(classId).trim();
  if (str.length === 0) return 'Unknown';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Formats the calling name along with its original archetype in parentheses,
 * e.g. 'Unseen (Rogue)', 'Wayfinder (Hunter)', 'Steadfast (Warrior)'.
 * Used in item tooltips and equipment requirements for clarity.
 */
export function formatCallingWithArchetype(classId: string | CharacterClass | null | undefined): string {
  if (!classId) return 'Unknown';
  const resolved = resolveToCharacterClass(classId);
  if (!resolved || resolved === CharacterClass.DEV) {
    return formatCallingName(classId);
  }
  const meta = CALLING_DISPLAY_REGISTRY[resolved];
  const callingName = meta ? meta.name : formatCallingName(classId);
  const archetype = resolved.charAt(0).toUpperCase() + resolved.slice(1).toLowerCase();
  if (callingName.toLowerCase() === archetype.toLowerCase()) {
    return callingName;
  }
  return `${callingName} (${archetype})`;
}

/** Returns the full calling metadata for a given class ID or alias. */
export function getCallingMeta(classId: string | CharacterClass | null | undefined): CallingDisplayMeta | undefined {
  const resolved = resolveToCharacterClass(classId);
  return resolved ? CALLING_DISPLAY_REGISTRY[resolved] : undefined;
}

/** Checks whether a given string corresponds to a known calling or alias. */
export function isKnownCalling(id: string | null | undefined): boolean {
  return resolveToCharacterClass(id) !== null;
}

/** Returns all 9 product calling definitions (excluding internal DEV class). */
export function getAllProductCallings(): readonly CallingDisplayMeta[] {
  return [
    CALLING_DISPLAY_REGISTRY[CharacterClass.WARRIOR],
    CALLING_DISPLAY_REGISTRY[CharacterClass.MAGE],
    CALLING_DISPLAY_REGISTRY[CharacterClass.HUNTER],
    CALLING_DISPLAY_REGISTRY[CharacterClass.PALADIN],
    CALLING_DISPLAY_REGISTRY[CharacterClass.ROGUE],
    CALLING_DISPLAY_REGISTRY[CharacterClass.SHAMAN],
    CALLING_DISPLAY_REGISTRY[CharacterClass.PRIEST],
    CALLING_DISPLAY_REGISTRY[CharacterClass.WARLOCK],
    CALLING_DISPLAY_REGISTRY[CharacterClass.DRUID],
  ];
}
